import { LoadingButton, TextField, toast, Typography } from '@montugroup/design-system';
import { useCallback, useEffect, useState } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { useForm, useWatch } from 'react-hook-form';
import type { NavigateFunction } from 'react-router-dom';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { AuthCardContainer, AuthPageContainer, Background, CentreContainer } from '@/components/auth/Styles';

import settings from '@/constants/constants';
import { Logger } from '@/utils/logger';

import { usePortalPartnerBrand } from '@/hooks/portal-partner-brand';
import { FORGOT_PASSWORD_ROUTE } from '@/routes/route-paths';
import { zodResolver } from '@hookform/resolvers/zod';
import { UserPasswordSchema } from '@montugroup/circuit-api-contracts';
import { escriptConformanceId, ShowPasswordAdornment } from '@montugroup/circuit-shared';
import { PasswordRequirements } from '@montugroup/circuit-shared/components/common/PasswordRequirements';
import { BrandThemeProvider } from '@montugroup/themes';
import { Box, Stack } from '@mui/material';
import { z } from 'zod';

const logger = new Logger('ResetPassword');
const { url } = settings;

const DUMMY_EMAIL = 'example@montu.com.au';

export interface ResetPasswordFormData {
  password: string;
  confirmPassword: string;
}

enum PasswordErrorCode {
  PASSWORD_REUSE = 'PasswordReuseError',
  CURRENT_PASSWORD = 'CurrentPasswordError',
  REQUIREMENTS_NOT_MET = 'password.requirements_not_met',
}

const formSchema = z
  .object({
    password: UserPasswordSchema,
    confirmPassword: z.string().min(1, { message: 'Please confirm your password' }),
  })
  .superRefine((values, ctx) => {
    if (values.confirmPassword !== values.password) {
      ctx.addIssue({
        message: 'Passwords do not match',
        code: 'custom',
        path: ['confirmPassword'],
      });
    }
  });

const validateExpiryEpoch = (epochDate: number, navigate: NavigateFunction): void => {
  const now = new Date().toISOString();
  const nowEpoch = Date.parse(now);
  if (nowEpoch > epochDate) {
    toast.error(
      `Sorry, your password reset link has expired or is invalid. Please try again or contact our support on ${settings.support.phone.display}`,
      { autoClose: false },
    );
    navigate(FORGOT_PASSWORD_ROUTE);
  }
};

export function ResetPasswordPage() {
  const { key: brandName, Icon: Bg, Logo: TopLogo } = usePortalPartnerBrand();
  const params = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState<boolean>(false);
  const [message, setMessage] = useState<boolean>(false);
  const [isPasswordRevealed, setIsPasswordRevealed] = useState<boolean>(false);
  const [isConfirmPasswordRevealed, setIsConfirmPasswordRevealed] = useState<boolean>(false);

  const getToken = useCallback(() => {
    const hashParams = new URLSearchParams(location.hash.slice(1));

    return hashParams.get('token') || params?.id || '';
  }, [location.hash, params?.id]);

  const { register, handleSubmit, formState, control } = useForm<ResetPasswordFormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  });

  const password = useWatch({ name: 'password', control });
  const shouldShowForm = !message || password === '';
  const { errors } = formState;

  const onSubmit: SubmitHandler<ResetPasswordFormData> = async (formData: ResetPasswordFormData) => {
    setLoading(true);

    try {
      // TODO: Refactor to use ts-rest api client or at least axios api client if ts-rest is difficult
      const res = await fetch(`${url}/user/passwordupdate/${getToken()}`, {
        method: 'PUT',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'Conformance-Id': escriptConformanceId,
        },
        body: JSON.stringify({
          // NOTE: An email is required here because we need to send an email key on the body
          email: DUMMY_EMAIL,
          password: formData.password,
        }),
      });

      const result = await res.json();

      if (res.status === 400) {
        switch (result.error_code) {
          case PasswordErrorCode.PASSWORD_REUSE:
            toast.error('This password has been used before. Please choose a different password.');
            break;
          case PasswordErrorCode.CURRENT_PASSWORD:
            toast.error('This is your current password. Please choose a different password.');
            break;
          default:
            toast.error(result.message || 'Invalid password. Please try again.');
        }
        setLoading(false);
        return;
      }

      if (res.status === 403) {
        switch (result.error.code) {
          case PasswordErrorCode.REQUIREMENTS_NOT_MET:
            toast.error(
              'The password you entered is invalid as it does not meet our security requirements. Please choose a different password.',
            );
            break;
          default:
            toast.error(result.message || result.error.message || 'Invalid password. Please try again.');
        }

        setLoading(false);
        return;
      }

      if (res.status !== 200) {
        throw Error(result.message);
      }
      setMessage((msg) => !msg);
      toast.success('Password changed successfully');
      navigate('/');
    } catch (err) {
      toast.error(`Something went wrong. Please try again or contact support on ${settings.support.phone.display}`);
      setLoading(false);
      logger.error(err);
    }
  };

  useEffect(() => {
    const token = getToken().split('$');
    if (token?.length === 3) {
      const epochExpiry = Number(token[2]);

      validateExpiryEpoch(epochExpiry, navigate);
    }
  }, [getToken, navigate, params.id]);

  return (
    <BrandThemeProvider theme={brandName}>
      <AuthPageContainer>
        <CentreContainer>
          <AuthCardContainer>
            <Stack mb={10}>
              <TopLogo width="150px" height="auto" fillColor={(theme) => theme.palette.primary.main} />
            </Stack>

            <div>
              {shouldShowForm && (
                <Box component="form" onSubmit={handleSubmit(onSubmit)} data-private method="post">
                  <Stack spacing={4}>
                    <Typography sx={(theme) => ({ color: theme.palette.primary.main })} variant="h5" align="left">
                      Reset Password
                    </Typography>
                    <Typography
                      sx={(theme) => ({
                        color: theme.palette.primary.main,
                        paddingTop: theme.spacing(1),
                        paddingBottom: theme.spacing(1),
                      })}
                      align="left"
                    >
                      Reset your pharmacist account password? Please enter your new password below. Your password must:
                      <PasswordRequirements />
                    </Typography>

                    <TextField
                      {...register('password')}
                      error={!!errors.password}
                      helperText={errors.password?.message}
                      type={isPasswordRevealed ? 'text' : 'password'}
                      id="userPassword"
                      placeholder="Enter password"
                      label="New Password"
                      fullWidth
                      data-testid="new-password-form-input"
                      InputLabelProps={{
                        shrink: true,
                      }}
                      InputProps={{
                        endAdornment: (
                          <ShowPasswordAdornment isRevealed={isPasswordRevealed} toggle={setIsPasswordRevealed} />
                        ),
                      }}
                    />

                    <TextField
                      {...register('confirmPassword')}
                      error={!!errors.confirmPassword}
                      helperText={errors.confirmPassword?.message}
                      type={isConfirmPasswordRevealed ? 'text' : 'password'}
                      id="userConfirmPassword"
                      placeholder="Enter password"
                      label="Confirm Password"
                      fullWidth
                      data-testid="confirm-password-form-input"
                      InputLabelProps={{
                        shrink: true,
                      }}
                      InputProps={{
                        endAdornment: (
                          <ShowPasswordAdornment
                            isRevealed={isConfirmPasswordRevealed}
                            toggle={setIsConfirmPasswordRevealed}
                          />
                        ),
                      }}
                    />

                    <LoadingButton
                      id="btnSubmit"
                      loading={loading}
                      variant="contained"
                      color="secondary"
                      fullWidth
                      type="submit"
                      data-testid="submit-button"
                    >
                      Reset
                    </LoadingButton>
                  </Stack>
                </Box>
              )}
            </div>
          </AuthCardContainer>
        </CentreContainer>
      </AuthPageContainer>
      <Background>
        <Bg />
      </Background>
    </BrandThemeProvider>
  );
}

export default ResetPasswordPage;
